/**
 * Settings → Access control panel.
 *
 * Single unified view, no tabs:
 *   - Live search input (top-left) filters by name, UPN, or email.
 *   - SegmentedControl (top-right): All | Readers | Editors | Admins.
 *     "All" shows every member across the three roles, one row per
 *     (user, role) pair, with the role rendered as a coloured badge.
 *   - Add Member is an inline expanding panel; when filter is "All"
 *     it includes a small role picker so the admin chooses where the
 *     new member goes.
 *   - Remove confirmation is per-row inline (no nested modal).
 *   - Audit log is a collapsible footer section.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconSearch,
  IconTrash,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import type { ApiClient } from "../../api/client";

type RoleKey = "admins" | "editors" | "readers";
type FilterKey = "all" | RoleKey;

const HELP: Record<RoleKey, string> = {
  admins:  "Manage app-wide configuration. Cannot remove the last admin.",
  editors: "Read + modify rows where SQL grants allow.",
  readers: "Read-only access where SQL grants allow.",
};

const HELP_ALL =
  "All members across the three roles. Use the role picker on the right to filter.";

/** Display label + Mantine color for the access-level badge. */
const ROLE_BADGE: Record<RoleKey, { label: string; color: string }> = {
  admins:  { label: "Admin",  color: "indigo" },
  editors: { label: "Editor", color: "teal"   },
  readers: { label: "Reader", color: "gray"   },
};

const ROLE_ORDER: RoleKey[] = ["readers", "editors", "admins"];

type Member = {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
};

type AuditEntry = {
  id: number;
  occurredAt: string;
  actorUpn: string | null;
  action: string;
  targetGroup: string | null;
  targetUser: string | null;
};

export function AccessControlPanel({
  api,
  currentUserOid,
}: {
  api: ApiClient;
  currentUserOid: string | undefined;
}) {
  const qc = useQueryClient();

  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<RoleKey>("readers");
  const [showAudit, setShowAudit] = useState(false);
  // The role new adds get assigned to. Synced to the segment when the
  // segment is a concrete role; defaulted to "readers" when "All".
  const [addRole, setAddRole] = useState<RoleKey>("readers");

  // Fetch all three lists upfront so role filtering / All view are
  // both instant. Each list is small.
  const adminsQ = useQuery({
    queryKey: ["group-members", "admins"],
    queryFn: () => api.get<Member[]>("/api/groups/admins/members"),
  });
  const editorsQ = useQuery({
    queryKey: ["group-members", "editors"],
    queryFn: () => api.get<Member[]>("/api/groups/editors/members"),
  });
  const readersQ = useQuery({
    queryKey: ["group-members", "readers"],
    queryFn: () => api.get<Member[]>("/api/groups/readers/members"),
  });
  const queries: Record<RoleKey, typeof adminsQ> = {
    admins: adminsQ,
    editors: editorsQ,
    readers: readersQ,
  };

  // Sync the add-target role to the active segment whenever the admin
  // narrows the view to a specific role.
  useEffect(() => {
    if (filter !== "all") setAddRole(filter);
  }, [filter]);

  const addMut = useMutation({
    mutationFn: ({ role: r, user }: { role: RoleKey; user: Member }) =>
      api.post<{ ok: boolean }>(`/api/groups/${r}/members`, { userId: user.id }),
    onSuccess: (_data, vars) => {
      qc.setQueryData<Member[]>(["group-members", vars.role], (old) => {
        if (!old) return [vars.user];
        if (old.some((m) => m.id === vars.user.id)) return old;
        return [...old, vars.user].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );
      });
      qc.invalidateQueries({ queryKey: ["audit"] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["group-members", vars.role] });
      }, 5_000);
      setAdding(false);
      notifications.show({ message: "Member added", color: "teal" });
    },
    onError: (err) =>
      notifications.show({ message: `Could not add: ${(err as Error).message}`, color: "red" }),
  });

  // Change a member's role: add to the new group, then remove from
  // the old. Sequence matters — add first so we never leave the user
  // momentarily roleless. If the remove fails (e.g. last-admin guard),
  // they keep both memberships and the admin can resolve.
  const changeRoleMut = useMutation({
    mutationFn: async ({
      user, fromRole, toRole,
    }: { user: Member; fromRole: RoleKey; toRole: RoleKey }) => {
      await api.post<{ ok: boolean }>(`/api/groups/${toRole}/members`, { userId: user.id });
      await api.del(`/api/groups/${fromRole}/members/${user.id}`);
    },
    onSuccess: (_data, vars) => {
      qc.setQueryData<Member[]>(["group-members", vars.toRole], (old) => {
        if (!old) return [vars.user];
        if (old.some((m) => m.id === vars.user.id)) return old;
        return [...old, vars.user].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );
      });
      qc.setQueryData<Member[]>(["group-members", vars.fromRole], (old) =>
        old ? old.filter((m) => m.id !== vars.user.id) : old,
      );
      qc.invalidateQueries({ queryKey: ["audit"] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["group-members", vars.toRole] });
        qc.invalidateQueries({ queryKey: ["group-members", vars.fromRole] });
      }, 5_000);
      setEditingKey(null);
      notifications.show({ message: "Role changed", color: "teal" });
    },
    onError: (err, vars) => {
      // Re-pull truth from Graph immediately on failure — the server
      // state may now be in a partial-success limbo (e.g. add succeeded
      // but remove was blocked by the last-admin guard).
      qc.invalidateQueries({ queryKey: ["group-members", vars.toRole] });
      qc.invalidateQueries({ queryKey: ["group-members", vars.fromRole] });
      notifications.show({
        message: `Could not change role: ${(err as Error).message}`,
        color: "red",
      });
    },
  });

  const removeMut = useMutation({
    mutationFn: ({ role: r, userId }: { role: RoleKey; userId: string }) =>
      api.del(`/api/groups/${r}/members/${userId}`),
    onSuccess: (_data, vars) => {
      qc.setQueryData<Member[]>(["group-members", vars.role], (old) =>
        old ? old.filter((m) => m.id !== vars.userId) : old,
      );
      qc.invalidateQueries({ queryKey: ["audit"] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["group-members", vars.role] });
      }, 5_000);
      setConfirmingKey(null);
      notifications.show({ message: "Member removed", color: "teal" });
    },
    onError: (err) =>
      notifications.show({ message: `Could not remove: ${(err as Error).message}`, color: "red" }),
  });

  // Build the displayed list. Each entry is a (member, role) tuple so
  // the "All" view can show the same user multiple times when they're
  // in multiple groups, and per-row Remove knows which group to hit.
  const displayed = useMemo<{ member: Member; role: RoleKey; key: string }[]>(() => {
    const items: { member: Member; role: RoleKey; key: string }[] = [];
    const rolesToShow: RoleKey[] = filter === "all" ? ROLE_ORDER : [filter];
    for (const r of rolesToShow) {
      for (const m of queries[r].data ?? []) {
        items.push({ member: m, role: r, key: `${r}:${m.id}` });
      }
    }
    items.sort((a, b) => {
      const n = a.member.displayName.localeCompare(b.member.displayName);
      if (n !== 0) return n;
      return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
    });
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(({ member: m }) =>
      m.displayName.toLowerCase().includes(q) ||
      m.userPrincipalName.toLowerCase().includes(q) ||
      (m.mail ?? "").toLowerCase().includes(q),
    );
  }, [filter, search, queries.admins.data, queries.editors.data, queries.readers.data]);

  // Loading: in "All" view we wait for all three queries; otherwise
  // just the active one.
  const loading =
    filter === "all"
      ? adminsQ.isLoading || editorsQ.isLoading || readersQ.isLoading
      : queries[filter].isLoading;

  return (
    <Stack gap="md">
      {/* Top row: search (left) + filter segment (right). */}
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search members…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />
        <SegmentedControl
          value={filter}
          onChange={(v) => {
            setFilter(v as FilterKey);
            setConfirmingKey(null);
            setEditingKey(null);
          }}
          data={[
            { label: "All",     value: "all"     },
            { label: "Readers", value: "readers" },
            { label: "Editors", value: "editors" },
            { label: "Admins",  value: "admins"  },
          ]}
        />
      </Group>

      {/* Help text + Add member control. */}
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Text size="sm" c="dimmed" style={{ maxWidth: 480 }}>
          {filter === "all" ? HELP_ALL : HELP[filter]}
        </Text>
        <Button
          leftSection={adding ? <IconX size={14} /> : <IconUserPlus size={14} />}
          variant={adding ? "default" : "filled"}
          size="sm"
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? "Cancel" : "Add member"}
        </Button>
      </Group>

      {adding && (
        <UserSearchInline
          api={api}
          // Only exclude users already in the TARGET role; "all" lets
          // them be added to a different group than they're in.
          excludeIds={new Set((queries[addRole].data ?? []).map((m) => m.id))}
          busy={addMut.isPending}
          onPick={(u) => addMut.mutate({ role: addRole, user: u })}
          showRolePicker={filter === "all"}
          addRole={addRole}
          onAddRoleChange={setAddRole}
        />
      )}

      {loading ? (
        <Group justify="center" py="lg"><Loader size="sm" /></Group>
      ) : (
        <Table withRowBorders highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th style={{ width: 140 }}>Access level</Table.Th>
              <Table.Th style={{ width: 220 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayed.map(({ member: m, role: rowRole, key }) => {
              const isConfirming = confirmingKey === key;
              const isEditing = editingKey === key;
              const badge = ROLE_BADGE[rowRole];
              return (
                <Table.Tr key={key}>
                  <Table.Td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap">
                        <Text fw={500} size="sm" lh={1.25}>{m.displayName}</Text>
                        {m.id === currentUserOid && (
                          <Badge size="xs" variant="light" color="gray">You</Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" style={{ fontFamily: "var(--font-mono)" }}>
                        {m.userPrincipalName}
                      </Text>
                    </div>
                  </Table.Td>
                  <Table.Td>
                    {isEditing ? (
                      <SegmentedControl
                        size="xs"
                        value={pendingRole}
                        onChange={(v) => setPendingRole(v as RoleKey)}
                        data={[
                          { label: "Reader", value: "readers" },
                          { label: "Editor", value: "editors" },
                          { label: "Admin",  value: "admins"  },
                        ]}
                        disabled={changeRoleMut.isPending}
                      />
                    ) : (
                      <Badge size="sm" variant="light" color={badge.color}>{badge.label}</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap" justify="flex-end">
                      {isEditing ? (
                        <>
                          <Button
                            size="compact-xs"
                            variant="default"
                            onClick={() => setEditingKey(null)}
                            disabled={changeRoleMut.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="compact-xs"
                            color="indigo"
                            disabled={pendingRole === rowRole}
                            loading={changeRoleMut.isPending}
                            onClick={() => {
                              if (pendingRole === rowRole) {
                                setEditingKey(null);
                                return;
                              }
                              changeRoleMut.mutate({
                                user: m,
                                fromRole: rowRole,
                                toRole: pendingRole,
                              });
                            }}
                          >
                            Save
                          </Button>
                        </>
                      ) : isConfirming ? (
                        <>
                          <Text size="xs" c="dimmed">Remove?</Text>
                          <Button
                            size="compact-xs"
                            variant="default"
                            onClick={() => setConfirmingKey(null)}
                            disabled={removeMut.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="compact-xs"
                            color="red"
                            onClick={() => removeMut.mutate({ role: rowRole, userId: m.id })}
                            loading={removeMut.isPending}
                          >
                            Confirm
                          </Button>
                        </>
                      ) : (
                        <>
                          <Tooltip label="Change role">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => {
                                setConfirmingKey(null);
                                setPendingRole(rowRole);
                                setEditingKey(key);
                              }}
                            >
                              <IconEdit size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={`Remove from ${ROLE_BADGE[rowRole].label}`}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => {
                                setEditingKey(null);
                                setConfirmingKey(key);
                              }}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
            {displayed.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    {search ? "No matches." : "No members yet."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      )}

      <Divider my={4} />
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed">Audit log</Text>
        <Button
          variant="subtle"
          size="compact-xs"
          rightSection={showAudit ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
          onClick={() => setShowAudit((v) => !v)}
        >
          {showAudit ? "Hide" : "Show"}
        </Button>
      </Group>
      {showAudit && <AuditPanel api={api} />}
    </Stack>
  );
}

/**
 * Inline user-picker. When `showRolePicker` is true (i.e. the parent's
 * filter is "All"), an embedded SegmentedControl lets the admin choose
 * which role new adds go into.
 */
function UserSearchInline({
  api,
  excludeIds,
  busy,
  onPick,
  showRolePicker,
  addRole,
  onAddRoleChange,
}: {
  api: ApiClient;
  excludeIds: Set<string>;
  busy: boolean;
  onPick: (u: Member) => void;
  showRolePicker: boolean;
  addRole: RoleKey;
  onAddRoleChange: (r: RoleKey) => void;
}) {
  const [q, setQ] = useState("");
  const usersQ = useQuery({
    enabled: q.trim().length >= 2,
    queryKey: ["user-search", q],
    queryFn: () => api.get<Member[]>(`/api/users/search?q=${encodeURIComponent(q)}`),
  });

  const results = (usersQ.data ?? []).filter((u) => !excludeIds.has(u.id));

  return (
    <Paper withBorder p="sm" radius="sm" bg="var(--bg-sunk)">
      <Stack gap="xs">
        {showRolePicker && (
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>Add as</Text>
            <SegmentedControl
              size="xs"
              value={addRole}
              onChange={(v) => onAddRoleChange(v as RoleKey)}
              data={[
                { label: "Reader", value: "readers" },
                { label: "Editor", value: "editors" },
                { label: "Admin",  value: "admins"  },
              ]}
            />
          </Group>
        )}
        <TextInput
          autoFocus
          leftSection={<IconSearch size={14} />}
          placeholder="Search the directory by name, email, or UPN…"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          disabled={busy}
        />
        {q.trim().length < 2 ? (
          <Text size="xs" c="dimmed">Type at least 2 characters.</Text>
        ) : usersQ.isLoading ? (
          <Group justify="center" py="md"><Loader size="xs" /></Group>
        ) : usersQ.error ? (
          <Text size="sm" c="red">{(usersQ.error as Error).message}</Text>
        ) : results.length === 0 ? (
          <Text size="sm" c="dimmed">No matches.</Text>
        ) : (
          <Stack gap={2} mah={260} style={{ overflowY: "auto" }}>
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="kse-user-pick"
                disabled={busy}
                onClick={() => onPick(u)}
              >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <div>
                    <Text fw={500} size="sm">{u.displayName}</Text>
                    <Text size="xs" c="dimmed">{u.userPrincipalName}</Text>
                  </div>
                  {busy ? <Loader size="xs" /> : <IconCheck size={14} color="var(--fg-muted)" />}
                </Group>
              </button>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function AuditPanel({ api }: { api: ApiClient }) {
  const auditQ = useQuery({
    queryKey: ["audit"],
    queryFn: () => api.get<AuditEntry[]>("/api/audit"),
  });

  if (auditQ.isLoading) {
    return <Group justify="center" py="md"><Loader size="xs" /></Group>;
  }

  return (
    <Table withRowBorders highlightOnHover verticalSpacing={4}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>When</Table.Th>
          <Table.Th>Actor</Table.Th>
          <Table.Th>Action</Table.Th>
          <Table.Th>Group</Table.Th>
          <Table.Th>User</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {auditQ.data?.map((a) => (
          <Table.Tr key={a.id}>
            <Table.Td><Text size="xs" c="dimmed">{new Date(a.occurredAt).toLocaleString()}</Text></Table.Td>
            <Table.Td><Text size="sm">{a.actorUpn ?? "—"}</Text></Table.Td>
            <Table.Td><Badge size="sm" variant="light">{a.action}</Badge></Table.Td>
            <Table.Td><Text size="sm"><code>{a.targetGroup}</code></Text></Table.Td>
            <Table.Td><Text size="xs" c="dimmed">{a.targetUser ?? "—"}</Text></Table.Td>
          </Table.Tr>
        ))}
        {auditQ.data && auditQ.data.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={5}>
              <Text size="sm" c="dimmed" ta="center" py="md">No events yet.</Text>
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
}
